import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Download,
  IdCard,
  ShieldCheck,
  Sun,
  Lightbulb,
  Zap,
  CheckCircle2,
} from "lucide-react";
import SectionHeader from "../components/SectionHeader";
import CategoryCard from "../components/CategoryCard";
import CTASection from "../components/CTASection";
import Seo from "../components/Seo";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { COMPANY, TRUST_BADGES, WHAT_WE_DO, WHY_US } from "../lib/constants";
import useSiteSettings, { profilePdfUrl } from "../lib/useSiteSettings";

export default function Home() {
  const settings = useSiteSettings();
  const pdf = profilePdfUrl(settings);
  const waLink = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
    "Hello Melopa Invents, I have a requirement."
  )}`;
  return (
    <div>
      <Seo
        title="Government Contracting & Institutional Supplies"
        description="Bengaluru-based government contractor and institutional supply company offering renewable energy, solar lighting, electrical, CCTV, IT, furniture, kitchen and FMCG procurement."
        path="/"
      />
      {/* Hero */}
      <section className="hero-gradient text-white">
        <div className="container-x py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="kicker text-[var(--melopa-gold-soft)] mb-3">Government Contractor · Bengaluru</div>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight tracking-tight">
              Government Contracting, Institutional Supplies & Infrastructure Solutions
            </h1>
            <p className="text-white/80 mt-4 text-base md:text-lg max-w-2xl">
              Reliable procurement and project fulfilment partner for government departments, institutions, schools, hostels, corporates, and bulk buyers.
            </p>
            <p className="text-white/65 mt-3 text-sm md:text-base max-w-2xl">
              Melopa Invents Pvt Ltd supports requirement-based fulfilment across renewable energy, rooftop solar, solar street lights, high mast lighting, electrical systems, CCTV, smart classroom equipment, furniture, kitchen supplies, FMCG, beverages, dairy products, stationery, and other institutional requirements.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/submit" className="btn-gold" data-testid="hero-submit">
                Submit Requirement <ArrowRight className="w-4 h-4" />
              </Link>
              {pdf ? (
                <a href={pdf} target="_blank" rel="noreferrer" className="btn-outline-white" data-testid="hero-download">
                  <Download className="w-4 h-4" /> Download Company Profile
                </a>
              ) : (
                <Link to="/downloads" className="btn-outline-white" data-testid="hero-download">
                  <Download className="w-4 h-4" /> Download Company Profile
                </Link>
              )}
              <Link to="/card" className="btn-outline-white" data-testid="hero-card">
                <IdCard className="w-4 h-4" /> View Digital Card
              </Link>
              <a href={waLink} target="_blank" rel="noreferrer" className="btn-outline-white" data-testid="hero-wa">
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp Now
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-elevated">
              <img
                src="/img/hero-solar-rooftop.png"
                alt="Solar power infrastructure on institutional rooftop"
                className="w-full h-64 md:h-80 object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3b]/85 via-[#0b1f3b]/30 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
                <span className="badge-soft !bg-white/10 !text-white !border-white/20"><Sun className="w-3.5 h-3.5 text-[var(--melopa-gold-soft)]" /> Rooftop Solar</span>
                <span className="badge-soft !bg-white/10 !text-white !border-white/20"><Lightbulb className="w-3.5 h-3.5 text-[var(--melopa-gold-soft)]" /> Solar Street Lighting</span>
                <span className="badge-soft !bg-white/10 !text-white !border-white/20"><ShieldCheck className="w-3.5 h-3.5 text-[var(--melopa-gold-soft)]" /> CCTV / Security</span>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mt-4 backdrop-blur">
              <div className="text-[var(--melopa-gold-soft)] kicker mb-3">Company Snapshot</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["CIN", COMPANY.cin],
                  ["GSTIN", COMPANY.gstin],
                  ["MSME", COMPANY.msme],
                  ["FSSAI", COMPANY.fssai],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-[10px] uppercase tracking-widest text-white/60">{k}</div>
                    <div className="text-xs md:text-sm font-semibold text-white break-all">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-white/70">
                <span className="text-[var(--melopa-gold-soft)]">📍</span>
                {COMPANY.address}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="section-sm border-b border-soft">
        <div className="container-x">
          <div className="text-center mb-6">
            <div className="kicker mb-2">Trusted Profile</div>
            <h2 className="section-title text-2xl md:text-3xl">Government & Institutional Ready</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2" data-testid="trust-badges">
            {TRUST_BADGES.map((b) => (
              <span key={b} className="badge-soft">
                <CheckCircle2 className="w-3.5 h-3.5 text-[var(--melopa-green)]" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="section">
        <div className="container-x">
          <SectionHeader
            kicker="What We Do"
            title="Multi-Category Procurement & Infrastructure"
            subtitle="From rooftop solar to FMCG — a single partner for institutional, government, and bulk requirements."
          />
          <div className="grid-cards mt-8">
            {WHAT_WE_DO.map((c) => (
              <CategoryCard
                key={c.title}
                title={c.title}
                desc={c.desc}
                icon={c.icon}
                to="/products"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="section bg-soft">
        <div className="container-x grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeader
              kicker="Why Melopa Invents"
              title="Built For Institutional & Tender Buyers"
              subtitle="We combine multi-category sourcing, logistics capability, and partner-based execution to deliver reliably across departments and bulk requirements."
            />
          </div>
          <ul className="list-tick text-[15px]">
            {WHY_US.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Renewable energy highlight */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <SectionHeader
              kicker="Renewable Energy & Public Infrastructure"
              title="Solar, Lighting & Electrical Infrastructure"
              subtitle="Rooftop solar, solar street lights, high mast lighting, LED lighting, battery backup, CCTV, and institutional electrical infrastructure for public bodies, schools, hostels and offices."
            />
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/renewable-energy" className="btn-primary">
                Explore Renewable Energy <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products" className="btn-outline">
                View All Products & Services
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {[
              { icon: Sun, label: "Rooftop Solar", img: "/img/rooftop-solar.png" },
              { icon: Lightbulb, label: "Solar Street Lights", img: "/img/solar-street-light.png" },
              { icon: Zap, label: "Power Backup", img: "/img/high-mast-lighting.png" },
              { icon: ShieldCheck, label: "CCTV & Security", img: "/img/cctv-security.png" },
            ].map((it) => (
              <div key={it.label} className="card-premium overflow-hidden">
                <div className="relative h-32 w-full">
                  <img src={it.img} alt={it.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                  <div className="absolute top-2 left-2 w-9 h-9 rounded-lg bg-white/95 grid place-items-center"><it.icon className="w-4 h-4 text-navy" /></div>
                </div>
                <div className="p-4">
                  <div className="font-semibold text-navy text-sm">{it.label}</div>
                  <p className="text-xs text-[var(--melopa-muted)] mt-1">Supply, sourcing & execution through qualified partners.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead capture CTA */}
      <CTASection
        title="Have a Requirement or Tender Enquiry?"
        description="Submit your product, infrastructure, or institutional supply requirement. Our team will review the details and respond with suitable fulfilment support."
        primaryLabel="Submit Requirement"
      />

      {/* Digital card CTA */}
      <section className="section-sm">
        <div className="container-x grid md:grid-cols-2 gap-6 items-center">
          <div className="card-premium p-6">
            <div className="kicker mb-2">Digital Business Card</div>
            <h3 className="text-2xl font-bold text-navy">Share Melopa Invents Instantly</h3>
            <div className="divider-gold" />
            <p className="text-[var(--melopa-muted)]">
              Access our digital business card with call, WhatsApp, email, website, QR code, company profile download, and quick sharing options.
            </p>
            <Link to="/card" className="btn-primary mt-4">
              <IdCard className="w-4 h-4" /> Open Digital Business Card
            </Link>
          </div>
          <div className="card-premium p-6">
            <div className="kicker mb-2">Downloads</div>
            <h3 className="text-2xl font-bold text-navy">Company Profile & Vendor Documents</h3>
            <div className="divider-gold" />
            <div className="flex flex-wrap gap-2">
              {pdf ? (
                <a href={pdf} target="_blank" rel="noreferrer" className="btn-primary"><Download className="w-4 h-4" /> Download Company Profile</a>
              ) : (
                <Link to="/downloads" className="btn-outline"><Download className="w-4 h-4" /> Download Company Profile</Link>
              )}
              <Link to="/products" className="btn-outline">View Product Categories</Link>
              <Link to="/downloads" className="btn-outline">Request Vendor Documents</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
